import React, { useEffect, useState } from 'react'
import Slider from 'rc-slider'
import Select from 'react-select'
import ToastService from '../../../../services/toast.service'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import ChallengeCardSimple from '../../challenges/ChallengeCardSimple'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faArrowRight,
	faExclamationTriangle,
	faTimes,
} from '@fortawesome/pro-light-svg-icons'
import { countWords } from '../../../../utils/helpers'

interface QuestionFormElementProps {
	questionData: any
	value: any
	onChange: (value) => void
	validator?: any
	classes?: any
}

const QuestionFormElement = ({
	questionData,
	value,
	onChange,
	validator,
	classes = {},
}: QuestionFormElementProps) => {
	const [currentValue, setCurrentValue] = useState(value)
	const [rankingItems, setRankingItems] = useState([])
	const [currentQuestionId, setCurrentQuestionId] = useState(null)

	const handleSelectChange = (option) => {
		// In case of multi select this option is an array so we need to map it
		const newVal = Array.isArray(option) ? option.map((o) => o.id) : option.id
		setCurrentValue(newVal)
		onChange && onChange({ ...questionData, answer: newVal })
	}

	const handleSliderChange = (value) => {
		setCurrentValue(value)
		onChange && onChange({ ...questionData, answer: value })
	}

	const addToRanking = (item) => {
		if (rankingItems.length === questionData.options.rankingCount) {
			ToastService.showError('Your ranking is full')
			return
		}
		let currentState = rankingItems
		currentState.push({ ...item })
		setRankingItems([...currentState])
	}

	const removeFromRanking = (event: any, item: any) => {
		event.preventDefault()

		const currentItem = item
		const rankingItms = rankingItems
		rankingItms.splice(rankingItems.indexOf(currentItem), 1)
		setRankingItems([...rankingItms])

		if (rankingItms.length > 0) {
			triggerRankingChange()
		}

		return false
	}

	const triggerRankingChange = () => {
		let answerData = {}
		rankingItems.forEach((element, index) => {
			answerData[index] = element.id
		})
		onChange &&
			onChange({
				...questionData,
				answer: rankingItems.length > 0 ? answerData : null,
			})
	}

	// a little function to help us with reordering the result
	const reorder = (list, startIndex, endIndex) => {
		const result = Array.from(list)
		const [removed] = result.splice(startIndex, 1)
		result.splice(endIndex, 0, removed)

		return result
	}

	const getItemStyle = (isDragging, draggableStyle) => ({
		// some basic styles to make the items look a bit nicer
		userSelect: 'none',

		marginLeft: isDragging ? '-64px' : '0',

		// change background colour if dragging
		background: isDragging ? '#90cdf4' : '#fff',

		// styles we need to apply on draggables
		...draggableStyle,
	})

	const getListStyle = (isDraggingOver) => ({
		background: isDraggingOver ? '#edf2f7' : '#f7fafc',
	})

	const onDragEnd = (result) => {
		// dropped outside the list
		if (!result.destination) {
			return
		}

		const items = reorder(
			rankingItems,
			result.source.index,
			result.destination.index
		)

		setRankingItems([...items])
	}

	useEffect(() => {
		// On switching of questionData set new defaults
		if (currentQuestionId !== questionData.id) {
			if (value) {
				setCurrentQuestionId(questionData.id)
				setCurrentValue(value)
				let rankingValue = Array.isArray(value) ? value : Object.values(value)
				if (questionData.type === 'ranking' && rankingValue.length > 0) {
					setRankingItems([
						...rankingValue.map((itemId) =>
							questionData.options.items.find((opt) => opt.id === itemId)
						),
					])
				} else if (value.length === 0) {
					setRankingItems([])
				}

				if (questionData.type.includes('multi') && value.length > 0) {
					setCurrentValue([
						...value.map((itemId) =>
							questionData.options.items.find((opt) => opt.id === itemId)
						),
					])
				}
			} else {
				setCurrentValue(null)
			}
		}
	}, [questionData, value])

	useEffect(() => {
		// Don't trigger on rankingItems if the question is not a ranking question
		if (questionData.type !== 'ranking') {
			return
		}

		if (rankingItems.length > 0) {
			triggerRankingChange()
		}
	}, [rankingItems])

	const wordCount =
		questionData.type === 'text'
			? Boolean(currentValue) && countWords(currentValue || '')
			: -1

	const isMultiSelect = questionData.type.includes('multi')

	const getSelectValue = () => {
		const optionsExists =
			questionData && questionData.options && questionData.options.items
		return isMultiSelect
			? optionsExists &&
					questionData.options.items.filter(
						(item) =>
							(currentValue &&
								Array.isArray(currentValue) &&
								currentValue.indexOf(item.id) > -1) ||
							(value && Array.isArray(value) && value.indexOf(item.id) > -1)
					)
			: optionsExists &&
					questionData.options.items.filter(
						(item) => item.id === currentValue || item.id === value
					)
	}

	return (
		<div className="flex flex-col md:flex-row items-start py-2 flex-wrap border-b">
			<div className={`w-full mb-4 ${classes?.questionHolder ?? 'md:w-3/4'}`}>
				<header className="py-2">
					<h3
						className={`text-gray-800 font-semibold ${
							classes?.questionTitle ?? 'text-sm'
						}`}
					>
						<span className="text-red-500">
							{questionData.options.required && '* '}
						</span>
						{questionData.title}
						{questionData.options.excludeRoles?.includes('student') && (
							<p className="block text-gray-500 text-xs mt-2">
								Not visible to students
							</p>
						)}
					</h3>
				</header>
				{questionData.type === 'text' && (
					<div className="space-y-4">
						<div>
							{questionData?.options?.maxWords !== undefined && (
								<span className="text-gray-500 inline-block mb-2 text-sm">
									Maximum words: <b>{questionData?.options?.maxWords}</b>.{' '}
									<span
										className={`${
											wordCount > questionData.options.maxWords &&
											'text-red-500'
										}`}
									>
										Words left:{' '}
										<b>{questionData.options.maxWords - wordCount}</b>
									</span>
								</span>
							)}
							<textarea
								value={currentValue || ''}
								onChange={(event) => {
									setCurrentValue(event.target.value)
									onChange &&
										onChange({ ...questionData, answer: event.target.value })
								}}
								id=""
								className="w-full h-40"
							></textarea>
							{questionData?.options?.maxWords !== undefined &&
								wordCount > questionData.options.maxWords && (
									<p className="text-red-500 my-4">
										<FontAwesomeIcon icon={faExclamationTriangle} /> Your answer
										contains too many words.
									</p>
								)}
						</div>
					</div>
				)}
				{questionData.type.startsWith('select') && (
					<div className="relative">
						<Select
							isClearable={false}
							value={getSelectValue()}
							isMulti={isMultiSelect}
							menuPosition={'fixed'}
							getOptionValue={(option) => option.id}
							defaultOptions
							options={questionData.options.items}
							instanceId={questionData.id}
							className="w-full"
							classNamePrefix="react-select"
							isSearchable={true}
							styles={{
								input: (provided) => ({
									...provided,
									'input:focus': {
										boxShadow: 'none',
									},
								}),
							}}
							onChange={handleSelectChange}
							placeholder={
								isMultiSelect
									? 'Select one or more options'
									: 'Select an option'
							}
						/>
					</div>
				)}
				{questionData.type.startsWith('scale') && (
					<div className="pt-4 pb-8 px-2 pr-10">
						<Slider
							min={questionData.options.min || 0}
							marks={{
								[questionData.options.min || 0]: {
									style: {
										whiteSpace: 'nowrap',
										transform: 'translateX(-5%)',
										color: '#454545',
									},
									label: questionData.options.minLabel || 'Strongly disagree',
								},
								[questionData.options.max || 5]: {
									style: {
										whiteSpace: 'nowrap',
										transform: 'translateX(-90%)',
										color: '#454545',
									},
									label: questionData.options.maxLabel || 'Strongly agree',
								},
							}}
							dotStyle={{
								width: 13,
								height: 13,
								bottom: -4,
								marginLeft: -6.5,
							}}
							included={false}
							defaultValue={null}
							onChange={handleSliderChange}
							value={currentValue}
							dots
							max={questionData.options.max || 5}
							handleStyle={{
								backgroundColor: '#6435D1',
								borderColor: '#6435D1',
								width: '18px',
								height: '18px',
								marginTop: '-8px',
							}}
						/>
					</div>
				)}
				{questionData.type === 'ranking' && (
					<div>
						{questionData.type === 'ranking' &&
							questionData.options.items.map((opt) => {
								return (
									rankingItems.map((c) => c.id).indexOf(opt.id) == -1 && (
										<div key={opt.id} className="relative mb-2">
											<div className="relative border shadow rounded px-4 py-2">
												{opt.label}
												<button
													onClick={() => addToRanking(opt)}
													type="button"
													className="bg-green-300 py-2 px-3 hover:bg-green-400 text-green-600 shadow absolute"
													style={{ right: '10px', bottom: '3px' }}
												>
													<FontAwesomeIcon icon={faArrowRight} />
												</button>
											</div>
										</div>
									)
								)
							})}

						{questionData.type === 'ranking' &&
							!questionData.options.items.filter(
								(item) => rankingItems.map((c) => c.id).indexOf(item.id) < 0
							).length && <div className="w-full py-2">No items available</div>}
					</div>
				)}
			</div>
			{questionData.type === 'ranking' && (
				<div className={`w-full md:w-1/2 md:pl-4`}>
					<h2 className="mb-2 font-semibold text-sm">Your ranking</h2>
					<p className="text-gray-700 mb-2">
						Please make a{' '}
						<strong>top {questionData.options.rankingCount}</strong>
					</p>
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="droppable" placeholder="Drop items here">
							{(provided, snapshot) => (
								<div
									{...provided.droppableProps}
									ref={provided.innerRef}
									className="rounded border border-dashed w-full p-2 mb-2"
									style={getListStyle(snapshot.isDraggingOver)}
								>
									{rankingItems.length > 0 &&
										rankingItems.map((item, index) => (
											<Draggable
												key={item.id}
												draggableId={item.id}
												index={index}
											>
												{(provided, snapshot) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														className="bg-white relative rounded shadow border border-gray-200"
														style={getItemStyle(
															snapshot.isDragging,
															provided.draggableProps.style
														)}
													>
														<div className="p-2 border-b flex flex-nowrap items-stretch">
															<div className="rounded-l w-8 bg-white -mb-px border -mr-1 relative z-10 cursor-pointer flex flex-col">
																<span className="flex flex-grow items-center justify-center w-full">
																	{index + 1}
																</span>
																<button
																	onClick={(e) => {
																		e.stopPropagation()
																		removeFromRanking(e, item)
																	}}
																	className="p-2 rounded-none z-10 w-full border-red-200 border-t text-red-500 bg-red-100 hover:bg-red-300 flex items-center justify-center"
																>
																	<FontAwesomeIcon icon={faTimes} />
																</button>
															</div>
															{questionData.type === 'ranking' ? (
																//Component for displaying default ranking options
																<div className="flex items-start pl-4 flex-col flex-grow border rounded-tr-xl rounded-br-xl">
																	{item.label && (
																		<p className="h-full flex items-center">
																			{item.label}
																		</p>
																	)}
																</div>
															) : // Doing a attribute check, child is rendered based on question type
															// Item is still being updated after rendered child is changed which passes old props to new component
															// If current item has no attribues (expected in challenge) render a null
															item.attributes ? (
																<ChallengeCardSimple challenge={item} />
															) : null}
														</div>
													</div>
												)}
											</Draggable>
										))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
			)}
			{questionData.options.required && validator && (
				<span className="block">
					{validator.message('question', currentValue, 'required')}
				</span>
			)}
		</div>
	)
}

export default QuestionFormElement
